/**
 * USP store – single source of truth for artist USP (unique selling proposition).
 * Same pattern as personalDetailsStore: ensureFetched on mount, update on save.
 */

import { create } from "zustand";
import { ArtistService } from "@/services/artistService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";
import type { UspData, UspPayload } from "@/services/artistService";

export type { UspData, UspPayload };

interface UspState {
  data: UspData | null;
  isLoading: boolean;
  saveLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: UspData | null) => void;
  fetchUsp: () => Promise<UspData | null>;
  ensureFetched: () => Promise<UspData | null>;
  updateUsp: (payload: UspPayload) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightFetch: Promise<UspData | null> | null = null;

const initialState = {
  data: null,
  isLoading: false,
  saveLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useUspStore = create<UspState>()((set, get) => ({
  ...initialState,

  setData: (data) => set({ data, error: null }),

  fetchUsp: async () => {
    if (inFlightFetch) return inFlightFetch;
    inFlightFetch = (async () => {
      set({ isLoading: true, error: null });
      try {
        const data = await ArtistService.getUsp();
        set({
          data,
          error: null,
          isLoading: false,
          hasFetchedInSession: true,
        });
        void useArtistDetailsStore.getState().fetchArtistDetails();
        return data;
      } catch (err) {
        set({ error: "Failed to load USP", isLoading: false });
        return null;
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchUsp, data } = get();
    if (hasFetchedInSession) return data;
    return fetchUsp();
  },

  updateUsp: async (payload) => {
    set({ saveLoading: true, error: null });
    try {
      await ArtistService.updateUsp(payload);
      await get().fetchUsp();
    } catch (err) {
      set({
        error: "Failed to save. Please try again.",
        saveLoading: false,
      });
      throw err;
    } finally {
      set({ saveLoading: false });
    }
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  reset: () => {
    inFlightFetch = null;
    set(initialState);
  },
}));
